import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, InputGroup, FormControl, Badge} from 'react-bootstrap';
import {
  FaUser, FaGraduationCap, FaCalendar, FaBook, FaSearch, 
  FaBuilding, FaUsers, FaChalkboardTeacher, FaListOl,
  FaUserGraduate, FaClock, FaUniversity, FaBullhorn,
  FaToggleOn, FaToggleOff
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import axios from '../api/axios'; // Assuming this is correctly configured
import GenericCrudPage from '../components/GenericCrudPage';
import GenericViewModal from '../components/GenericViewModal';
import {
  useColleges,
  useDegrees,
  useAcademicYears,
  useStaffTypes,
  useProgrammes,
  useCourses,
  useSemesters,
  useProgrammeYears,
  useExamAnnouncements,
  useStaff,
  useDepartments,
  useDesignations,
  useDisciplines,
  useSections
} from '../hooks/useDropdownData';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const REGISTRATION_TYPE_OPTIONS = [
  { value: 1, label: 'Regular' },
  { value: 2, label: 'Supplementary' },
  { value: 3, label: 'Reappear' }
];

const TeacherCourseMapping = () => {
  const [filterDegree, setFilterDegree] = useState(""); 
  const [searchMappingId, setSearchMappingId] = useState("");
  const [showMappingViewModal, setShowMappingViewModal] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);

  // Load all dropdown data
  const { colleges, loading: collegesLoading } = useColleges();
  const { degrees, loading: degreesLoading } = useDegrees();
  const { academicYears, loading: academicYearsLoading } = useAcademicYears();
  const { staffTypes, loading: staffTypesLoading } = useStaffTypes();
  const { programmes, loading: programmesLoading } = useProgrammes();
  const { courses, loading: coursesLoading } = useCourses();
  const { semesters, loading: semestersLoading } = useSemesters();
  const { programmeYears, loading: programmeYearsLoading } = useProgrammeYears();
  const { examAnnouncements, loading: examAnnouncementsLoading } = useExamAnnouncements();
  const { departments, loading: departmentsLoading } = useDepartments();
  const { designations, loading: designationsLoading } = useDesignations();
  const { disciplines, loading: disciplinesLoading } = useDisciplines();
  const { sections, loading: sectionsLoading } = useSections();
  
  
  // Pass the `filterDegree` state to the useStaff hook.
  const { staff, loading: staffLoading } = useStaff(filterDegree);

  const dropdownsLoading = collegesLoading || degreesLoading || academicYearsLoading || 
                           staffTypesLoading || programmesLoading || coursesLoading ||
                           semestersLoading || programmeYearsLoading || examAnnouncementsLoading ||
                           departmentsLoading || designationsLoading || disciplinesLoading || sectionsLoading;

  // This useEffect automatically selects the first degree when the page loads.
  useEffect(() => {
    if (!degreesLoading && degrees.length > 0 && !filterDegree) {
      console.log("Degrees loaded. Setting initial degree to:", degrees[0].id);
      setFilterDegree(degrees[0].id.toString());
    }
  }, [degrees, degreesLoading, filterDegree]);

  // Custom fetch function for the main data table (TeacherCourseMapping)
  const customFetch = async () => {
    if (!filterDegree) {
        console.log("Main fetch is waiting for a degree to be selected.");
        return [];
    }

    const token = Cookies.get('token');
    const getByIdUrl = `${BASE_URL}/academic/getCourseMappingOnID`;

    // Helper to fetch full details for each mapping to get course info
    const fetchFullMappingDetails = async (mappingList) => {
      if (!mappingList || mappingList.length === 0) return [];
      try {
        const fullMappings = await Promise.all(
          mappingList.map(mapping =>
            axios.get(`${getByIdUrl}?id=${mapping.id}`, { headers: { Authorization: `Bearer ${token}` } })
              .then(res => {
                const mappingData = res.data?.teacherCourseMapping;
                return Array.isArray(mappingData) ? mappingData[0] : mappingData;
              })
              .catch(err => {
                console.error(`Failed to fetch full details for mapping ID ${mapping.id}`, err);
                return mapping; // On error, return original (partial) data
              })
          )
        );
        return fullMappings.filter(Boolean); // Filter out any null/undefined results
      } catch (error) {
        console.error("Error during bulk mapping detail fetch:", error);
        return mappingList; // Return original list on catastrophic failure
      }
    };

    if (searchMappingId) {
      try {
        const res = await axios.get(`${getByIdUrl}?id=${searchMappingId}`, { headers: { Authorization: `Bearer ${token}` } });
        const mappingData = res.data?.teacherCourseMapping;
        return mappingData ? (Array.isArray(mappingData) ? mappingData : [mappingData]) : [];
      } catch (error) {
        toast.error("Could not fetch mapping for the provided ID.");
        return [];
      }
    } else {
      try {
        const getAllUrl = `${BASE_URL}/academic/getTeacherCourseMapping?degree=${filterDegree}`;
        console.log("Fetching teacher course mappings for degree:", filterDegree);
        const res = await axios.get(getAllUrl, { headers: { Authorization: `Bearer ${token}` } });
        return res.data?.teacherCourseMapping || [];
      } catch (error) {
        toast.error("Could not fetch teacher course mapping data.");
        return [];
      }
    }
  };

  // ✅ CORRECTED: This function now correctly handles the API response for a single item.
  const fetchMappingById = async (item) => {
    if (!item || !item.id) {
      toast.error("Invalid mapping selected.");
      return null;
    }
    try {
      const token = Cookies.get('token');
      const res = await axios.get(`${BASE_URL}/academic/getCourseMappingOnID?id=${item.id}`, { headers: { Authorization: `Bearer ${token}` } });
      // The API returns an array named 'teacherCourseMapping', so we take the first element.
      const mappingData = res.data?.teacherCourseMapping;
      return Array.isArray(mappingData) && mappingData.length > 0 ? mappingData[0] : null;
    } catch (error) {
      toast.error("An error occurred while fetching mapping details.");
      return null;
    }
  };

  const validateForm = (form) => {
    const errors = {};
    if (!form.academicYear) errors.academicYear = 'Academic Year is required';
    if (!form.colleges) errors.colleges = 'College is required';
    if (!form.examAnnouncement) errors.examAnnouncement = 'Exam Announcement is required';
    if (!form.registrationType) errors.registrationType = 'Registration Type is required';
    if (!form.staffType) errors.staffType = 'Staff Type is required';
    if (!form.staffpersnoal) errors.staffpersnoal = 'Staff Person is required';
    if (!form.programmes) errors.programmes = 'Programme is required';
    if (!form.degree) errors.degree = 'Degree is required';
    if (!form.programmeYear) errors.programmeYear = 'Programme Year is required';
    if (!form.semester) errors.semester = 'Semester is required';
    if (!form.courses || form.courses.length === 0) errors.courses = 'At least one course is required';
    return errors;
  };

  const config = {
    baseUrl: BASE_URL,
    endpoints: {
      fetch: '/academic/getTeacherCourseMapping',
      save: '/academic/saveTeacherCourseMapping',
      update: '/academic/updateTeacherCourseMapping',
      delete: '/academic/deleteTeacherCourseMapping'
    },
    validateForm,
    initialFormState: {
      academicYear: "", colleges: "", examAnnouncement: "", registrationType: "",
      staffType: "", staffpersnoal: "", programmes: "", section: "", courses: [],
      degree: "", programmeYear: "", semester: "", exIsActive: true, user: 1,
    },
    entityName: 'TeacherCourseMapping',
    fetchMethod: 'CUSTOM',
    deleteMethod: 'GET',
    customFetch,
  };

  const transformPayload = (form, editId, userId) => {
    const payload = {
      academicYear: Number(form.academicYear),
      colleges: Number(form.colleges),
      Degree: Number(form.degree),
      programmes: Number(form.programmes),
      ProgrammeYear: Number(form.programmeYear),
      Semester: Number(form.semester),
      StaffType: Number(form.staffType),
      staffpersnoal: Number(form.staffpersnoal),
      section: Number(form.section),
      examAnnouncement: Number(form.examAnnouncement),
      registrationType: Number(form.registrationType),
      exIsActive: form.exIsActive ? true : false,
      courses: Array.isArray(form.courses) ? form.courses.map(id => Number(id)) : [],
      user: Number(userId) || 1,
    };
    if (editId) {
        payload.id = editId;
    }
    console.log("Final payload being sent to server:", payload);
    return payload;
  };

  // ✅ CORRECTED: This function now correctly maps the flat API response to the form state.
  const transformToForm = (item) => {
    if (!item) return config.initialFormState;
    console.log("Transforming item for edit form:", item);
    return {
      ...config.initialFormState,
      academicYear: item.academicyearid?.toString() || "", 
      colleges: item.collegeid?.toString() || "",
      examAnnouncement: item.examId?.toString() || "", 
      registrationType: item.registrationTypeId?.toString() || "",
      staffType: item.staffTypeId?.toString() || "", 
      staffpersnoal: item.staffId?.toString() || "",
      programmes: item.programeId?.toString() || "", 
      degree: item.degreeid?.toString() || "",
      programmeYear: item.programmeyearid?.toString() || "", 
      semester: item.semesterid?.toString() || "",
      courses: Array.isArray(item.coursesId) ? item.coursesId.map(id => id.toString()) : [],
      section: item.sectionId?.toString() || "", 
      exIsActive: item.exIsActive === true || item.exIsActive === 1,
    };
  };

  const formFields = [
    {
      title: '🏢 Academic Information',
      fields: [
        { name: 'academicYear', label: 'Academic Year', icon: FaCalendar, required: true, as: 'select',
          options: (academicYears || []).map(ay => ({ value: ay.id, label: ay.exYear })) },
        { name: 'colleges', label: 'College', icon: FaBuilding, required: true, as: 'select',
          options: (colleges || []).map(c => ({ value: c.id, label: c.exName })) },
        { name: 'degree', label: 'Degree', icon: FaGraduationCap, required: true, as: 'select',
          options: (degrees || []).map(d => ({ value: d.id, label: d.exName })) },
        { name: 'programmes', label: 'Programme', icon: FaUserGraduate, required: true, as: 'select',
          options: (programmes || []).map(p => ({ value: p.id, label: p.exName })) },
        { name: 'programmeYear', label: 'Programme Year', icon: FaListOl, required: true, as: 'select',
          options: (programmeYears || []).map(py => ({ value: py.id, label: py.exName })) },
        { name: 'semester', label: 'Semester', icon: FaClock, required: true, as: 'select',
          options: (semesters || []).map(s => ({ value: s.id, label: s.exName })) },
      ]
    },
    {
      title: '👨‍🏫 Staff & Course Assignment',
      fields: [
        { name: 'staffType', label: 'Staff Type', icon: FaUsers, required: true, as: 'select',
          options: (staffTypes || []).map(st => ({ value: st.id, label: st.exName })) },
        { name: 'staffpersnoal', label: 'Staff Person', icon: FaChalkboardTeacher, required: true, as: 'select',
          options: (staff || []).map(s => ({ value: s.id, label: (`${s.firstName || ''} ${s.lastName || ''}`.trim()) || s.exName || `Staff ID: ${s.id}` })),
          loading: staffLoading },
        { name: 'courses', label: 'Courses', icon: FaBook, required: true, as: 'multiselect',
          options: (courses || []).map(c => ({ value: c.id.toString(), label: c.exName })) },
        { name: 'section', label: 'Section', icon: FaUsers, as: 'select',
          options: (sections || []).map(sec => ({ value: sec.id, label: sec.exName })) },
      ]
    },
    {
      title: '📋 Exam & Registration Details',
      fields: [
        { name: 'examAnnouncement', label: 'Exam Announcement', icon: FaBullhorn, required: true, as: 'select',
          options: (examAnnouncements || []).map(ea => ({ value: ea.id, label: ea.exName || ea.exDescription || `Announcement ID: ${ea.id}` })) },
        { name: 'registrationType', label: 'Registration Type', icon: FaUniversity, required: true, as: 'select',
          options: REGISTRATION_TYPE_OPTIONS },
        { name: 'exIsActive', label: 'Active Status', icon: FaToggleOn, as: 'switch' },
      ]
    }
  ];

  const tableColumns = [
    { key: 'id', label: 'Mapping ID', sortable: true },
    { key: 'academicyear', label: 'Academic Year', sortable: true },
    { key: 'college', label: 'College', sortable: true },
    { key: 'degree', label: 'Degree', sortable: true },
    { key: 'programe', label: 'Programme', sortable: true },
    { key: 'staffName', label: 'Staff', sortable: true },
    { 
      key: 'courses', 
      label: 'Courses', 
      sortable: false, // Sorting on an array is complex, disabled for clarity
      render: (item) => {
        // The API response for the list might not have course names, but IDs.
        // We use the `courses` list from the `useCourses` hook to find the names.
        const courseIds = item.coursesId || [];
        
        if (!Array.isArray(courseIds) || courseIds.length === 0) {
          // As a fallback, check for a 'courses' array of strings (from view/edit response)
          if (Array.isArray(item.courses) && item.courses.length > 0) {
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {item.courses.map((courseName, index) => (
                  <Badge key={index} bg="secondary" className="fw-normal">{courseName}</Badge>
                ))}
              </div>
            );
          }
          return <span className="text-muted">N/A</span>;
        }

        const courseNames = courseIds.map(id => {
          const course = (courses || []).find(c => c.id === id);
          return course ? course.exName : `ID: ${id}`;
        });

        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {courseNames.map((name, index) => (
              <Badge key={index} bg="secondary" className="fw-normal">{name}</Badge>
            ))}
          </div>
        );
      } 
    },
    { key: 'semester', label: 'Semester', sortable: true },
  ];

  const csvHeaders = [
    { key: 'id', label: 'Mapping ID' }, { key: 'academicyear', label: 'Academic Year' },
    { key: 'college', label: 'College' }, { key: 'degree', label: 'Degree' },
    { key: 'programe', label: 'Programme' }, { key: 'programmeyear', label: 'Programme Year' },
    { key: 'semester', label: 'Semester' }, { key: 'staffName', label: 'Staff Person' },
    { key: 'staffType', label: 'Staff Type' }, { key: 'courses', label: 'Courses' },
    { key: 'section', label: 'Section' }, { key: 'examName', label: 'Exam Announcement' },
    { key: 'registrationType', label: 'Registration Type' },
    { key: 'exIsActive', label: 'Status', render: (item) => item.exIsActive ? 'Active' : 'Inactive' }
  ];

  const getViewFields = (item) => {
    if (!item) return [];
    return [
      { label: 'Mapping ID', value: item.id, icon: FaListOl },
      { label: 'Academic Year', value: item.academicyear, icon: FaCalendar },
      { label: 'College', value: item.college, icon: FaBuilding },
      { label: 'Degree', value: item.degree, icon: FaGraduationCap },
      { label: 'Programme', value: item.programe, icon: FaUserGraduate },
      { label: 'Programme Year', value: item.programmeyear, icon: FaListOl },
      { label: 'Semester', value: item.semester, icon: FaClock },
      { label: 'Section', value: item.section, icon: FaUsers },
      { label: 'Staff Type', value: item.staffType, icon: FaUsers },
      { label: 'Staff Person', value: item.staffName, icon: FaChalkboardTeacher },
      { label: 'Courses', value: Array.isArray(item.courses) ? item.courses.join(', ') : 'N/A', icon: FaBook, fullWidth: true },
      { label: 'Exam Announcement', value: item.examName, icon: FaBullhorn },
      { label: 'Registration Type', value: item.registrationType, icon: FaUniversity },
      { label: 'Status', value: item.exIsActive ? 'Active' : 'Inactive', icon: item.exIsActive ? FaToggleOn : FaToggleOff, className: item.exIsActive ? 'text-success fw-bold' : 'text-danger fw-bold' },
    ];
  };

  const handleSearchById = async () => {
    if (!searchMappingId) {
      toast.error("Please enter a Mapping ID to search.");
      return;
    }
    try {
      const token = Cookies.get('token');
      const res = await axios.get(`${BASE_URL}/academic/getCourseMappingOnID?id=${searchMappingId}`, { headers: { Authorization: `Bearer ${token}` } });
      const mappingData = res.data?.teacherCourseMapping?.[0];
      if (mappingData) {
        setSelectedMapping(mappingData);
        setShowMappingViewModal(true);
      } else {
        toast.warn("Mapping not found with the provided ID.");
      }
    } catch (error) {
      toast.error("An error occurred while searching for the mapping.");
    }
  };

  const handleClearFilters = () => {
    setSearchMappingId('');
    if (degrees.length > 0) {
      setFilterDegree(degrees[0].id.toString());
    } else {
      setFilterDegree('');
    }
  };

  const handleExportData = (data) => {
    return data.map(item => ({
      ...item,
      statusText: item.exIsActive ? 'Active' : 'Inactive',
      coursesCount: Array.isArray(item.courses) ? item.courses.length : 0,
      registrationTypeName: REGISTRATION_TYPE_OPTIONS.find(rt => rt.value === item.registrationTypeId)?.label || item.registrationType,
    }));
  };

  return (
    <Container fluid className="py-4">
      {(dropdownsLoading && !filterDegree) ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading application data...</p>
        </div>
      ) : (
        <>
          <Row className="mb-4 align-items-end">
            <Col md={4}>
              <Form.Label className="fw-semibold"><FaGraduationCap className="me-2" />Filter by Degree</Form.Label>
              <Form.Select
                value={filterDegree}
                onChange={(e) => {
                  setFilterDegree(e.target.value);
                  setSearchMappingId('');
                }}
                className="form-select-lg"
                disabled={degreesLoading}
              >
                <option value="">{degreesLoading ? "Loading Degrees..." : "Select a Degree"}</option>
                {(degrees || []).map((d) => (<option key={d.id} value={d.id}>{d.exName}</option>))}
              </Form.Select>
            </Col>
            <Col md={5}>
              <Form.Label className="fw-semibold"><FaSearch className="me-2" />Search by Mapping ID</Form.Label>
              <InputGroup size="lg">
                <FormControl
                  placeholder="Enter Mapping ID"
                  value={searchMappingId}
                  onChange={(e) => setSearchMappingId(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') handleSearchById(); }}
                />
                <Button variant="outline-primary" onClick={handleSearchById} disabled={!searchMappingId.trim()}>
                  <FaSearch /> Search
                </Button>
              </InputGroup>
            </Col>
            <Col md={3} className="d-flex justify-content-start">
              <Button variant="outline-secondary" onClick={handleClearFilters} size="lg" className="d-flex align-items-center">
                <span className="me-2">🔄</span>
                Clear Filters
              </Button>
            </Col>
          </Row>
          
          <Row className="mb-4">
            <Col>
              <div className="bg-light rounded p-3 border">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="text-muted mb-1">Current Filter</h6>
                    <p className="mb-0 fw-semibold">
                      {filterDegree ? `Degree: ${degrees?.find(d => d.id == filterDegree)?.exName || 'Unknown'}` : 'No Degree Selected'}
                    </p>
                  </div>
                  <div className="text-end">
                    <FaChalkboardTeacher className="text-primary" size={24} />
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <GenericCrudPage
            key={filterDegree} 
            title="Teacher Course Mapping"
            config={config}
            formFields={formFields}
            tableColumns={tableColumns}
            csvHeaders={csvHeaders}
            csvFilename="teacher_course_mappings.csv"
            transformPayload={transformPayload}
            transformToForm={transformToForm}
            getViewFields={getViewFields}
            fetchItemForView={fetchMappingById}
            fetchItemForEdit={fetchMappingById}
            exportDataTransform={handleExportData}
            emptyIcon={FaChalkboardTeacher}
            emptyTitle="No Mappings Found"
            emptyMessage={filterDegree ? "No mappings found for the selected degree. Try creating one." : "Please select a degree to see mappings."}
          />

          <GenericViewModal
            show={showMappingViewModal}
            onHide={() => setShowMappingViewModal(false)}
            item={selectedMapping}
            title="Teacher Course Mapping Details"
            fields={getViewFields(selectedMapping)}
          />
        </>
      )}
    </Container>
  );
};

export default TeacherCourseMapping;
